/*
 * Copyright (C) 2026 - present Marek Kuzora - All Rights Reserved.
 */


import { css, styled, use, useCallback, useClient, useEffect } from 'fierry/react';

import drag from 'fierry/util/drag';

import Data from 'solis/react/board/data';
import Settings from 'solis/react/board/settings';
import HexGeometry from 'solis/react/board/geometry/hex';
import BoardScreen from 'solis/react/board/svg/screen.client';
import BoardOffset from 'solis/react/board/svg/offset.state';


const getGroupStyle = ({ screen, data }) =>
{
  const { x, y } = screen.getBoardOffset(data.getCenterDisplayHex());

  return { transform: `translate(${x}px, ${y}px)` };
};


const transitionConfig = ({ dnd }) => !dnd && css`
  transition-property:        transform;
  transition-duration:        var(--fierry-transition-duration);
  transition-timing-function: var(--fierry-transition-function);
`;


const Group = styled.g`
  ${transitionConfig}
`;


const OnDrag = ({ dx, dy }, dispatch) =>
{
  dispatch.move({ dx, dy });
};


const OnDragStart = (event, dispatch) =>
{
  dispatch.startDrag();
};


const OnDragEnd = (event, dispatch) =>
{
  dispatch.endDrag();
};


const OnSvg = (element, settings, onDrag, onDragStart, onDragEnd) =>
{
  if (element && settings.isPannable())
  {
    drag(element, onDrag, onDragStart, onDragEnd);
  }
};


const OnClick = (event, screen, data, settings) =>
{
  const displayHex = screen.getHexFromEvent(event, data.getCenterDisplayHex());

  settings.onSelect(data.getSelectableZone(displayHex), data.toEntityHex(displayHex));
};


const Component = ({ children, className, width, height, dnd, onSvg, onClick, ...props }) =>
(
  <svg ref = {onSvg} className = {className} width = {width} height = {height} onClick = {onClick}>
    <Group dnd = {dnd} style = {getGroupStyle(props)}>
      {children}
    </Group>
  </svg>
);


export default (props) =>
{
  const { width, height } = props;

  const [ data, settings, geometry, offset, dispatch ] = use(Data, Settings, HexGeometry, BoardOffset, BoardOffset.Dispatch);

  const screen = useClient(BoardScreen, { width, height, geometry, offset });

  const center = data.getCenterDisplayHex();

  useEffect(() => { dispatch.reset(); }, [ center.toString() ]);

  const onDrag      = useCallback(OnDrag,      [ dispatch ]);
  const onDragStart = useCallback(OnDragStart, [ dispatch ]);
  const onDragEnd   = useCallback(OnDragEnd,   [ dispatch ]);

  const onSvg = useCallback(OnSvg, [ settings, onDrag, onDragStart, onDragEnd ]);

  const onClick = useCallback(OnClick, [ screen, data, settings ]);

  return <Component {...props} data = {data} screen = {screen} dnd = {offset.dnd} onSvg = {onSvg} onClick = {onClick} />;
}
